import {
  ApiFehler,
  zaehlAntwortAlsErfassung,
  zaehlKoerper,
  type BedienerKurz,
  type ErfassungErgebnis,
  type ErfassungPosition,
  type ZaehlAntwort,
} from './api';
import type { ErfassungsAntwort, ErfassungsKoerper } from './lager-warteschlange';

/**
 * Client des Lagergeräte-Zugangs (Handterminal): spricht nur die Lagerrouten, die der
 * Server einem Lagergeräte-Schlüssel erlaubt (packages/api/src/lagergeraet.ts). Keine
 * Verkaufs-, Beleg- oder Zahlungsfunktion – der Server würde sie ohnehin mit 403 ablehnen.
 */
export interface GeraetSelbstauskunft {
  geraet: { id: number; unternehmerId: string; name: string };
  unternehmer: { name: string } | null;
  module: { lager: boolean; bediener: boolean };
}

/** Bediener mit den Lagerrechten (ältere API: fehlen = nein). */
export type LagerBediener = Omit<BedienerKurz, 'rechte'> & {
  rechte: BedienerKurz['rechte'] & { lager?: boolean; artikel?: boolean };
};

export interface GeraeteArtikel {
  id: number;
  bezeichnung: string;
  ean: string | null;
  gruppe: string;
  bruttoCent: number;
  bestandsfuehrung: boolean;
  mindestbestand: number | null;
  bestand: number | null;
  steuersatz?: string;
  artikelnummer?: string | null;
  barcode?: string | null;
  /** Für den Verkauf gesperrt (im Lager buchbar); Grund zur Anzeige. */
  gesperrt?: boolean;
  sperrgrund?: string | null;
}

export interface GeraeteCodeAuskunft {
  /** Gescannter Code (getrimmt) und – falls gültige EAN/UPC/GTIN – seine EAN-13-Form. */
  code?: string;
  ean: string | null;
  status?: 'gefunden' | 'mehrdeutig' | 'unbekannt';
  /** true = erst über die EAN-Normalisierung (UPC-A/GTIN-14) gefunden. */
  normalisiert?: boolean;
  ueber?: 'barcode' | 'ean' | 'artikelnummer';
  /** Mehrere Artikel passen nur über die Normalisierung – nichts wird zugeordnet. */
  mehrdeutig?: { id: number; bezeichnung: string; ean: string | null }[];
  artikel: GeraeteArtikel | null;
  vorschlag: { id: number; ean: string; bezeichnung: string | null; menge: number } | null;
  produkt: { bezeichnung: string } | null;
}

export interface GeraeteBewegung {
  id: number;
  art: 'wareneingang' | 'verkauf' | 'storno' | 'inventur' | 'korrektur';
  menge: number;
  bestandNachher: number;
  bezug: string | null;
  notiz: string | null;
  erstelltAm: string;
}

export interface GeraeteVorschlag {
  id: number;
  ean: string;
  bezeichnung: string | null;
  menge: number;
  bezug: string | null;
  erstelltAm: string;
}

export interface BestandZeile {
  id: number;
  bezeichnung: string;
  ean: string | null;
  gruppe: string;
  bestand: number;
  mindestbestand: number | null;
  unterMindestbestand: boolean;
}

function istRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

/** Offener Inventurlauf des Unternehmers (Kopfdaten). */
export interface GeraeteInventurlauf {
  id: number;
  bezeichnung: string;
  abgrenzungAm: string;
  status: string;
  abschluss?: { erstelltAm: string } | null;
  verworfen?: { erstelltAm: string } | null;
}

export class LagergeraetApi {
  private readonly basisUrl: string;
  private readonly fetchFn: typeof fetch;

  constructor(
    private readonly opt: {
      basisUrl: string;
      /** Lagergeräte-Schlüssel `lagergeraet_…`. */
      schluessel: () => string | null;
      /** Unternehmer laut Selbstauskunft (für die Routen). */
      unternehmerId: () => string | null;
      /** Signierte Bediener-Anmeldung (Kopf X-Bediener-Sitzung). */
      bedienerSitzung: () => string | null;
      fetchFn?: typeof fetch;
      /** Zeitlimit je Anfrage (Standard 15 s) – im Lager bricht WLAN gern ab. */
      zeitlimitMs?: number;
    },
  ) {
    this.basisUrl = opt.basisUrl.replace(/\/+$/, '');
    this.fetchFn = opt.fetchFn ?? ((eingabe, init) => fetch(eingabe, init));
  }

  private unternehmerPfad(): string {
    const u = this.opt.unternehmerId();
    if (u === null || u === '') {
      throw new ApiFehler(0, 'Gerät ist nicht eingerichtet');
    }
    return `/unternehmer/${encodeURIComponent(u)}`;
  }

  /** `pfad` als Funktion: ein fehlender Unternehmer wird so zur abgelehnten Promise, nie zum Wurf. */
  private async aufruf<T>(
    methode: 'GET' | 'POST',
    pfad: string | (() => string),
    body?: unknown,
  ): Promise<T> {
    const ziel = typeof pfad === 'string' ? pfad : pfad();
    const schluessel = this.opt.schluessel();
    const sitzung = this.opt.bedienerSitzung();
    const abbruch = new AbortController();
    const timer = setTimeout(() => {
      abbruch.abort();
    }, this.opt.zeitlimitMs ?? 15_000);
    try {
      const res = await this.fetchFn(`${this.basisUrl}${ziel}`, {
        method: methode,
        signal: abbruch.signal,
        headers: {
          Accept: 'application/json',
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(schluessel === null ? {} : { Authorization: `Bearer ${schluessel}` }),
          ...(sitzung === null ? {} : { 'X-Bediener-Sitzung': sitzung }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
      const text = await res.text();
      if (!res.ok) {
        let fehler = text;
        try {
          const json: unknown = JSON.parse(text);
          if (istRecord(json) && typeof json.fehler === 'string') {
            fehler = json.fehler;
          }
        } catch {
          // kein JSON – Text behalten
        }
        throw new ApiFehler(res.status, fehler || `HTTP ${String(res.status)}`);
      }
      return JSON.parse(text) as T;
    } finally {
      clearTimeout(timer);
    }
  }

  ich(): Promise<GeraetSelbstauskunft> {
    return this.aufruf('GET', '/lagergeraet/ich');
  }

  bediener(): Promise<{ bediener: LagerBediener[]; verwaltet: boolean }> {
    return this.aufruf('GET', () => `${this.unternehmerPfad()}/lager/geraet/bediener`);
  }

  bedienerAnmelden(
    id: number,
    pin: string | undefined,
  ): Promise<{ bediener: LagerBediener; sitzung: string }> {
    return this.aufruf(
      'POST',
      () => `${this.unternehmerPfad()}/lager/geraet/bediener/${String(id)}/anmeldung`,
      pin === undefined ? {} : { pin },
    );
  }

  codeAuskunft(code: string): Promise<GeraeteCodeAuskunft> {
    return this.aufruf(
      'GET',
      () => `${this.unternehmerPfad()}/lager/geraet/artikel/${encodeURIComponent(code)}`,
    );
  }

  suche(q: string): Promise<{ treffer: GeraeteArtikel[]; mehr: boolean }> {
    return this.aufruf(
      'GET',
      () => `${this.unternehmerPfad()}/lager/geraet/suche?q=${encodeURIComponent(q)}&limit=50`,
    );
  }

  bestand(
    offset: number,
    nurUnterMindestbestand: boolean,
  ): Promise<{ artikel: BestandZeile[]; gesamt: number; offset: number }> {
    const q = new URLSearchParams({ offset: String(offset), limit: '100' });
    if (nurUnterMindestbestand) {
      q.set('unterMindestbestand', '1');
    }
    return this.aufruf(
      'GET',
      () => `${this.unternehmerPfad()}/lager/geraet/bestand?${q.toString()}`,
    );
  }

  bewegungen(artikelId: number): Promise<{ bewegungen: GeraeteBewegung[]; gesamt: number }> {
    return this.aufruf(
      'GET',
      () =>
        `${this.unternehmerPfad()}/lager/geraet/bewegungen?artikelId=${String(artikelId)}&limit=30`,
    );
  }

  /**
   * Buchungsauftrag senden – Körper unverändert aus dem eingefrorenen Auftrag. Inventur im
   * Lauf geht an die Zählung des Laufs (Zählstelle = bezug, gleiche Vorgangskennung).
   */
  erfassung(koerper: ErfassungsKoerper): Promise<ErfassungsAntwort> {
    if (koerper.laufId !== undefined && koerper.art === 'inventur') {
      return this.zaehlungImLauf(koerper.laufId, koerper);
    }
    return this.aufruf('POST', () => `${this.unternehmerPfad()}/lager/erfassung`, koerper);
  }

  /** Inventurläufe des Unternehmers (neueste zuerst, höchstens 50). */
  async inventurlaeufe(): Promise<GeraeteInventurlauf[]> {
    const r = await this.aufruf<{ laeufe: GeraeteInventurlauf[] }>(
      'GET',
      () => `${this.unternehmerPfad()}/lager/inventurlaeufe`,
    );
    return r.laeufe;
  }

  /** Offener Inventurlauf des Unternehmers oder null. */
  async offenerInventurlauf(): Promise<GeraeteInventurlauf | null> {
    return (await this.inventurlaeufe()).find((l) => l.status === 'offen') ?? null;
  }

  private async zaehlungImLauf(
    laufId: number,
    koerper: ErfassungsKoerper,
  ): Promise<ErfassungsAntwort> {
    const r = await this.aufruf<ZaehlAntwort>(
      'POST',
      () => `${this.unternehmerPfad()}/lager/inventurlaeufe/${String(laufId)}/zaehlungen`,
      zaehlKoerper(koerper),
    );
    return zaehlAntwortAlsErfassung(r, koerper.positionen, laufId);
  }

  vorschlaege(): Promise<{
    vorschlaege: GeraeteVorschlag[];
    gruppen: string[];
    gruppenSteuersatz: Record<string, string | null>;
  }> {
    return this.aufruf('GET', () => `${this.unternehmerPfad()}/lager/geraet/vorschlaege`);
  }

  vorschlagUebernehmen(
    id: number,
    daten: {
      gruppe: string;
      bezeichnung: string;
      bruttoCent: number;
      steuersatz: string;
      /** Codex K-7: Vorgangskennung (Wiederholung bucht nicht doppelt). */
      vorgangId?: string;
    },
  ): Promise<{ bestand: number }> {
    return this.aufruf(
      'POST',
      () => `${this.unternehmerPfad()}/artikelvorschlaege/${String(id)}/uebernahme`,
      daten,
    );
  }

  vorschlagVerwerfen(id: number): Promise<unknown> {
    return this.aufruf(
      'POST',
      () => `${this.unternehmerPfad()}/artikelvorschlaege/${String(id)}/verwerfen`,
    );
  }
}

export type { ErfassungErgebnis, ErfassungPosition };
